<template>
  <el-form
    :model="form"
    autocomplete="off"
    class="lad-custom-filter-wrapper"
    @submit.native.prevent="() => false"
  >
    <LadFilterOrderBy
      :value="orderBy"
      :values="orderByOptions"
      @change="handleUpdateOrderBy"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.order_by.label`)
      }}</p>
    </LadFilterOrderBy>

    <LadFilterDurationRange
      :value="form.filters.duration"
      @update-values="handleUpdateDuration"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.duration.label`)
      }}</p>
    </LadFilterDurationRange>

    <LadFilterCity
      v-if="isCountry"
      :values="form.filters.city"
      @update-values="handleUpdateCity"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $tc(`${filterBase}.city.label`, form.filters.city.length, {
          qty: form.filters.city.length,
        })
      }}</p>
    </LadFilterCity>

    <LadFilterDate
      :is-custom="true"
      :values="form.daterange || []"
      :label="$t(`${filterBase}.date.advanced_label`)"
      :is-advanced="true"
      @update-values="handleUpdateDateRange"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.date.label`)
      }}</p>
    </LadFilterDate>

    <LadFilterMonth
      :values="form.filters.month"
      @update-values="handleUpdateMonth"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.month.label`)
      }}</p>
    </LadFilterMonth>

    <LadFilterPriceRange
      :value="form.filters.price"
      @update-values="handleUpdatePrice"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.price.label`)
      }}</p>
    </LadFilterPriceRange>

    <LadFilterAgeRange
      :value="form.filters.age"
      @update-values="handleUpdateAge"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.age.label`)
      }}</p>
    </LadFilterAgeRange>

    <LadFilterLanguage
      :values="form.filters.language"
      @update-values="handleUpdateLanguage"
    >
      <p slot="before-range" class="subtitle-filters">{{
        $t(`${filterBase}.language.label`)
      }}</p>
    </LadFilterLanguage>

    <div class="lad-filter--apply-filters">
      <el-button type="primary" @click="advancedSearch">{{
        $t(`${filterBase}.general.apply_filters`)
      }}</el-button>
      <el-button plain type="primary" @click="resetFilters">{{
        $t(`${filterBase}.general.reset`)
      }}</el-button>
    </div>
  </el-form>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import PriceMixin from '@/components/search/mixin/price'

export default {
  name: 'LadSearchCustom',
  components: {
    LadFilterDate: () =>
      import(
        /* webpackChunkName: "lad-search-filter-date" */ '../filters/date'
      ),
    LadFilterMonth: () =>
      import(
        /* webpackChunkName: "lad-search-filter-month" */ '../ranges/month'
      ),
    LadFilterCity: () =>
      import(/* webpackChunkName: "lad-search-filter-city" */ '../ranges/city'),

    LadFilterLanguage: () =>
      import(
        /* webpackChunkName: "lad-search-filter-languague" */ '../ranges/language'
      ),
    LadFilterPriceRange: () =>
      import(
        /* webpackChunkName: "lad-search-filter-price" */ '../ranges/price-range'
      ),
    LadFilterDurationRange: () =>
      import(
        /* webpackChunkName: "lad-search-filter-duration" */ '../ranges/duration-range'
      ),
    LadFilterOrderBy: () =>
      import(
        /* webpackChunkName: "lad-search-filter-order-by" */ '../ranges/order-by'
      ),
    LadFilterAgeRange: () =>
      import(
        /* webpackChunkName: "lad-search-filter-age" */ '../ranges/age-range'
      ),
  },
  mixins: [PriceMixin],
  props: {
    name: { type: String, required: true, default: '' },
  },
  data() {
    return {
      filterBase: 'filters',
      hasPriceFilter: false,
      hasMonthsFilter: false,
      hasCityFilter: false,
      hasLanguageFilter: false,
      hasPriceDuration: false,
      hasPriceAge: false,
      orderByOptions: ['price', 'popular', 'review'], // 'duration'
      orderBy: 'price',

      form: {
        query: null,
        selected: null,
        daterange: [],
        filters: {
          price: { min: null, max: null, type: 'price' },
          duration: [],
          age: [],
          month: [],
          city: [],
          language: [],
        },
      },
    }
  },
  computed: {
    ...mapGetters('device', ['isMobile']),
    ...mapState('search', {
      searchForm: 'form',
    }),
    isCountry() {
      const type = this.$route.query.type || this.$route.params.type

      return this.$route.name === 'search' && type === 'country'
    },
    type() {
      return this.hasPriceFilter ||
        this.hasPriceDuration ||
        this.hasPriceAge ||
        this.hasMonthsFilter ||
        this.hasCityFilter ||
        this.hasLanguageFilter
        ? 'success'
        : 'default'
    },
  },
  watch: {
    loading(newValue) {
      this.$emit('on-loading', newValue)
    },
  },
  beforeMount() {
    if (this.searchForm.filters.date.length > 0) {
      const _get = this.searchForm.filters.date
      this.form.daterange = Array.isArray(_get) ? _get : [_get]
    }

    if (
      this.searchForm.filters.price.min > 0 ||
      this.searchForm.filters.price.max > 0
    ) {
      const _get = this.searchForm.filters.price
      this.form.filters.price = _get
    } else {
      this.form.filters.price.min = this.min
      this.form.filters.price.max = this.max
    }

    if (this.searchForm.filters.duration.length > 0) {
      const _get = this.searchForm.filters.duration
      this.form.filters.duration = Array.isArray(_get) ? _get : [_get]
    } else {
      this.form.filters.duration = [
        this.searchValues.duration.min,
        this.searchValues.duration.max,
      ]
    }

    if (this.searchForm.filters.age.length > 0) {
      const _get = this.searchForm.filters.age
      this.form.filters.age = Array.isArray(_get) ? _get : [_get]
    } else {
      this.form.filters.age = [
        this.searchValues.age.min,
        this.searchValues.age.max,
      ]
    }

    if (this.searchForm.filters.month.length > 0) {
      const _get = this.searchForm.filters.month
      this.form.filters.month = Array.isArray(_get) ? _get : [_get]
    } else {
      this.form.filters.month = []
    }

    if (this.searchForm.filters.city.length > 0) {
      const _get = this.searchForm.filters.city
      this.form.filters.city = Array.isArray(_get) ? _get : [_get]
    } else {
      this.form.filters.city = []
    }

    if (this.searchForm.filters.language.length > 0) {
      const _get = this.searchForm.filters.language
      this.form.filters.language = Array.isArray(_get) ? _get : [_get]
    } else {
      this.form.filters.language = []
    }

    switch (this.searchForm.sort.term) {
      case 'price_promo':
        this.orderBy = 'price'
        break
      case 'more_popular':
        this.orderBy = 'popular'
        break
      case 'average_review':
        this.orderBy = 'review'
        break

      default:
        this.orderBy = 'price'
        break
    }
  },
  methods: {
    resetFilters() {
      this.form.daterange = []
      this.form.filters.price = { min: this.min, max: this.max, type: 'price' }

      this.form.filters.month = []
      this.form.filters.city = []

      this.form.filters.duration = [
        this.searchValues.duration.min,
        this.searchValues.duration.max,
      ]
      this.form.filters.age = [
        this.searchValues.age.min,
        this.searchValues.age.max,
      ]
      this.orderBy = 'price'
    },
    handleUpdatePrice(values) {
      this.form.filters.price = values

      this.hasPriceFilter = this.min !== values.min || this.max !== values.max
    },
    handleUpdateCity(values) {
      this.form.filters.city = values

      this.hasCityFilter = values.length > 0
    },
    handleUpdateMonth(values) {
      this.form.filters.month = values

      this.hasMonthsFilter = values.length > 0

      if (this.hasMonthsFilter) {
        this.form.daterange = []
      }
    },
    handleUpdateLanguage(values) {
      this.form.filters.language = values

      this.hasLanguageFilter = values.length > 0
    },
    handleUpdateDuration(values) {
      this.form.filters.duration = values

      this.hasPriceDuration =
        this.searchValues.duration.min !== values[0] ||
        this.searchValues.duration.max !== values[1]
    },
    handleUpdateAge(values) {
      this.form.filters.age = values

      this.hasPriceAge =
        this.searchValues.age.min !== values[0] ||
        this.searchValues.age.max !== values[1]
    },
    handleUpdateDateRange(values) {
      this.form.daterange = values

      if (this.form.daterange.length > 0) {
        this.form.filters.month = []

        this.hasMonthsFilter = false
      }
    },
    handleUpdateOrderBy(value) {
      if (this.orderBy !== value) {
        this.orderBy = value
      }
    },
    async advancedSearch() {
      if (!process.client) return

      const query = { q: '', type: 'experience' }

      /** adicionando filtros activos de forma dinamica */
      Object.keys(this.form.filters).forEach((k) => {
        if (k === 'price') {
          /** es un objeto y no un array */
          if (
            this.form.filters[k].min !== this.min ||
            this.form.filters[k].max !== this.max
          ) {
            query[k] = this.form.filters[k]
          }
        } else if (this.form.filters[k].length > 0) {
          if (
            ['month', 'city', 'language'].includes(k) ||
            this.form.filters[k][0] !== this.searchValues[k].min ||
            this.form.filters[k][this.form.filters[k].length - 1] !==
              this.searchValues[k].max
          ) {
            query[k] = this.form.filters[k]
          }
        }
      })

      const data = {
        filters: {
          date: [],
          price: this.hasPriceFilter
            ? this.form.filters.price
            : { min: null, max: null, type: 'price' },
          duration: this.hasPriceDuration ? this.form.filters.duration : [],
          age: this.hasPriceAge ? this.form.filters.age : [],
          category: [],
          month: this.hasMonthsFilter ? this.form.filters.month : [],
          city: this.hasCityFilter ? this.form.filters.city : [],
          language: this.hasLanguageFilter ? this.form.filters.language : [],
        },
        sort: 'price_promo__asc',
      }

      /** obteniendo rango de fecha seleccionado */
      if (this.form.daterange.length > 0) {
        query.date = [
          this.$dayjs(this.form.daterange[0]).format('YYYY-MM-DD'),
          this.$dayjs(
            this.form.daterange[this.form.daterange.length - 1]
          ).format('YYYY-MM-DD'),
        ]
        data.filters.date = query.date
      }

      switch (this.orderBy) {
        case 'price':
          data.sort = 'price_promo__asc'
          break
        case 'popular':
          data.sort = 'more_popular__desc'
          break
        case 'review':
          data.sort = 'average_review__desc'
          break
        case 'duration':
          data.sort = 'more_duration__desc'
          break

        default:
          data.sort = 'price_promo__asc'
          break
      }

      await this.$store.dispatch('search/setForm', data)

      this.$nuxt.$emit('handle-filters', {})
    },
  },
}
</script>

<style lang="scss">
.lad-custom-filter-wrapper {
  .subtitle-filters {
    font-size: 1.08em;
    font-weight: 600;
    margin-bottom: 0.8em;
  }

  div[class^='lad-filter--'] {
    & + div[class^='lad-filter--'] {
      margin-top: 20px;
    }
  }

  .lad-filter--price-content,
  .lad-filter--duration-content,
  .lad-filter--age-content {
    .wrapper-element-filter {
      .wrapper-min-max-labels {
        display: flex;
        justify-content: space-between;

        & > span {
          flex: 1;

          &:first-child {
            text-align: left;
          }

          &:last-child {
            text-align: right;
          }
        }
      }
    }

    .value-min,
    .value-max {
      font-size: 12px;
    }
  }

  .el-slider {
    &__marks-text {
      font-size: 12px;
      margin-top: 8px;
    }

    &__runway {
      .el-slider__button-wrapper {
        transform: translateX(-30%);

        & + .el-slider__button-wrapper {
          transform: translateX(-70%);
        }
      }
    }
  }

  .el-checkbox {
    display: block;
  }

  .lad-filter--date-range-content {
    display: grid;
    grid-template-columns: 50px 1fr;

    .el-date-editor {
      visibility: visible;
      position: relative;
      width: 100%;
      order: 2;
      display: grid;
      grid-template-columns: 1fr 10px 1fr;
      align-content: center;
      border-radius: unset;
      height: 45px;

      .el-input__icon {
        display: none;
      }

      input {
        width: 100%;
      }
    }

    .el-button {
      order: 1;
      padding: 10px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 0;

      i {
        display: inline-block;
      }

      span {
        display: none;
      }
    }
  }

  .lad-filter--apply-filters {
    .el-button {
      display: block;
      width: 100%;

      & + .el-button {
        margin: 10px 0 0;
      }

      &.is-plain {
        background-color: white;
        color: #409eff;

        &:hover,
        &:focus,
        &:active {
          color: #409eff;
        }
      }
    }
  }
}
</style>
